import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { Modal } from '@/components'
import { useFetchMessagingMethods } from '@/domain/messagingMethods'
import type { MessagingMethodsDTO } from '@/domain/messagingMethods/messagingMethodsTypes'
import { AppIcons } from '@/components'
import { Skeleton } from '@/components/Skeleton'
import { ChooseMessagingAppModalProps } from './types'

export const ChooseMessagingAppModal: React.FC<
  ChooseMessagingAppModalProps
> = ({ isOpen, onClose, onSelect }) => {
  const { t } = useTranslation()

  const {
    data: messagingMethodsData,
    isLoading,
    isError,
    refetch,
  } = useFetchMessagingMethods({
    is_available: true,
  })

  const messagingMethods = messagingMethodsData?.methods || []

  const handleMethodSelect = (method: MessagingMethodsDTO) => {
    onSelect({
      code: method.code,
      img_url: method.img_url,
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} showHandle={true}>
      <BaseView
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          paddingTop: 16,
          paddingBottom: 34,
        }}
      >
        {/* Header */}
        <BaseView
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          {/* Title */}
          <BaseText
            fontSize={28}
            fontFamily="semibold"
            color="label.default"
            textAlign="left"
            style={{ width: '100%' }}
          >
            {t('bottomSheets:chooseMessagingApp.title')}
          </BaseText>

          {/* Description */}
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.secondary"
            textAlign="left"
            style={{ width: '100%', lineHeight: '22px' }}
          >
            {t('bottomSheets:chooseMessagingApp.description')}
          </BaseText>
        </BaseView>

        {/* Methods List */}
        <BaseView
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {isLoading ? (
            <>
              <MessagingMethodItemSkeleton />
              <MessagingMethodItemSkeleton />
              <MessagingMethodItemSkeleton />
            </>
          ) : isError ? (
            <ErrorState onRetry={refetch} />
          ) : (
            messagingMethods.map((method) => (
              <MessagingMethodItem
                key={method.code}
                method={method}
                onPress={() => handleMethodSelect(method)}
              />
            ))
          )}
        </BaseView>
      </BaseView>
    </Modal>
  )
}

interface MessagingMethodItemProps {
  method: MessagingMethodsDTO
  onPress: () => void
}

const MessagingMethodItem: React.FC<MessagingMethodItemProps> = ({
  method,
  onPress,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 12,
        backgroundColor: '#232425',
        borderRadius: 24,
        cursor: method.available ? 'pointer' : 'not-allowed',
        opacity: method.available ? 1 : 0.5,
        height: 74,
        transition: 'background-color 0.2s ease',
      }}
      onClick={method.available ? onPress : undefined}
      onMouseEnter={(e) => {
        if (method.available) {
          e.currentTarget.style.backgroundColor = '#2a2b2c'
        }
      }}
      onMouseLeave={(e) => {
        if (method.available) {
          e.currentTarget.style.backgroundColor = '#232425'
        }
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={method.img_url}
            alt={method.name}
            style={{ width: 50, height: 50 }}
          />
        </div>

        <BaseText fontSize={18} fontFamily="semibold" color="label.default">
          {method.name}
        </BaseText>
      </div>

      <div style={{ width: 24, height: 24 }}>
        <AppIcons name="chevronRight" size={24} color="icon.iconCard" />
      </div>
    </div>
  )
}

const MessagingMethodItemSkeleton: React.FC = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 12,
        backgroundColor: '#232425',
        borderRadius: 24,
        height: 74,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Skeleton width={50} height={50} viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="25" />
        </Skeleton>
        <Skeleton width={120} height={20} viewBox="0 0 120 20">
          <rect rx={4} ry={4} width="120" height="20" />
        </Skeleton>
      </div>
      <Skeleton width={24} height={24} viewBox="0 0 24 24">
        <rect rx={4} ry={4} width="24" height="24" />
      </Skeleton>
    </div>
  )
}

interface ErrorStateProps {
  onRetry: () => void
}

const ErrorState: React.FC<ErrorStateProps> = ({ onRetry }) => {
  const { t } = useTranslation()

  return (
    <BaseView
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: 24,
      }}
    >
      {/* <BaseView
        style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AppIcons name="warning" size={24} color="red" />
      </BaseView> */}

      {/* <BaseView style={{ gap: 8 }}>
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          color="label.default"
          textAlign="center"
        >
          {t('bottomSheets:chooseMessagingApp.errorTitle')}
        </BaseText>
        <BaseText
          fontSize={14}
          fontFamily="regular"
          color="label.secondary"
          textAlign="center"
        >
          {t('bottomSheets:chooseMessagingApp.errorDescription')}
        </BaseText>
      </BaseView> */}

      <button
        onClick={onRetry}
        style={{
          padding: '12px 24px',
          backgroundColor: '#232425',
          borderRadius: 12,
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <BaseText fontSize={14} fontFamily="semibold" color="label.default">
          {t('bottomSheets:chooseMessagingApp.tryAgain')}
        </BaseText>
      </button>
    </BaseView>
  )
}
